// src/pages/ParittaWritingPage.js

import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";

import MasterLayoutGames from "../layouts/GamesLayout";

import data from "../data/parittaPuzzle.json";

import {
  toPinyinSync,
  capitalizePinyin,
} from "../utils/pinyinUtil";

const API_URL =
  process.env.REACT_APP_BACKEND_URL;

const STORAGE_KEY =
  "paritta_writing_storage_v1";

const GRID_TYPES = {
  tian: "田字格 Tian Zi Ge",
  mi: "米字格 Mi Zi Ge",
  empty: "空白 Kosong",
};

export default function ParittaWritingPage() {

  const [parittaIndex, setParittaIndex] =
    useState(0);

  const [pageIndex, setPageIndex] =
    useState(0);

  const [gridType, setGridType] =
    useState("tian");

  const [penColor, setPenColor] =
    useState("#222222");

  const [penSize, setPenSize] =
    useState(8);

  const [isEraser, setIsEraser] =
    useState(false);

  // default focus mode
  const [minimalMode, setMinimalMode] =
    useState(true);

  const [confirmClear, setConfirmClear] =
    useState(false);

  // modal
  const [
    showSettingsModal,
    setShowSettingsModal,
  ] = useState(false);

	const [showInstruction, setShowInstruction] =
  	useState(false);

  // visual config
  const [ghostSize, setGhostSize] =
    useState(3.7);

  const [gridSize, setGridSize] =
    useState(5.5);

  const [ghostFont, setGhostFont] =
    useState("Cursive");

  const currentGroup = data[parittaIndex];

  // ============================================
  // 1 ITEM = 1 PAGE
  // ============================================

  const pages = useMemo(() => {

    return currentGroup.items.map(
      (item) => item.text.split("")
    );

  }, [currentGroup]);

  const currentPageChars =
    pages[pageIndex] || [];

  const currentItem =
    currentGroup.items[pageIndex];

  const isSpaceChar = (char) =>
    char === " " || char === "　";

  // ============================================
  // PINYIN
  // ============================================

  const buildPinyin = (text) => {

    const raw = toPinyinSync(text);

    return capitalizePinyin(raw);
  };

  // ============================================
  // STORAGE
  // ============================================

  const getParittaStoragePrefix =
    () => `paritta_${parittaIndex}_`;

  const clearCurrentParitta = () => {

    try {

      const raw =
        localStorage.getItem(
          STORAGE_KEY
        );

      let parsed = {};

      try {
        parsed = raw
          ? JSON.parse(raw)
          : {};
      } catch (e) {}

      const nextData = {};

      Object.keys(parsed).forEach(
        (key) => {

          if (
            !key.startsWith(
              getParittaStoragePrefix()
            )
          ) {
            nextData[key] =
              parsed[key];
          }

        }
      );

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(nextData)
      );

      setConfirmClear(false);

      window.location.reload();

    } catch (e) {
      console.log(e);
    }
  };

  // ============================================
  // NAVIGATION
  // ============================================

  const nextPage = () => {

    if (
      pageIndex <
      currentGroup.items.length - 1
    ) {
      setPageIndex((p) => p + 1);
    }
  };

  const prevPage = () => {

    if (pageIndex > 0) {
      setPageIndex((p) => p - 1);
    }
  };

  return (

    <MasterLayoutGames title="✍️ 抄經練習 Latihan Menulis Paritta">

      {/* ================================= */}
      {/* HEADER */}
      {/* ================================= */}

      <div style={styles.header}>

        <div style={styles.titleChinese}>
          {currentGroup.title}
        </div>

      </div>

      {/* ================================= */}
      {/* TOP ACTION */}
      {/* ================================= */}

			<div style={styles.topActionRow}>

				<div style={styles.topLeftActions}>

					<button
						style={styles.secondaryTopBtn}
						onClick={() =>
							setShowSettingsModal(true)
						}
					>
						⚙️ 設定 Settings
					</button>

					<button
						style={styles.secondaryTopBtn}
						onClick={() =>
							setShowInstruction((v) => !v)
						}
					>
						{showInstruction
							? "隱藏說明 Hide Info"
							: "說明 Info"}
					</button>

				</div>

				{/* TOOLS */}

				<div style={styles.sizeRow}>

					<button
						onClick={() =>
							setIsEraser(false)
						}
						style={
							!isEraser
								? styles.sizeActive
								: styles.sizeBtn
						}
					>
						筆 Pen
					</button>

					<button
						onClick={() =>
							setIsEraser(true)
						}
						style={
							isEraser
								? styles.sizeActive
								: styles.sizeBtn
						}
					>
						橡皮擦 Eraser
					</button>

				</div>

				<div style={styles.pageInfo}>

					<span
						style={{
							marginRight: "1rem",
						}}
					>
						頁 Halaman:
					</span>

					{pageIndex + 1} /
					{currentGroup.items.length}

				</div>

			</div>
			
			{showInstruction && (

				<div style={styles.instructionCard}>

					<div style={styles.instructionTitle}>
						抄經前 Sebelum Menulis Paritta
					</div>

					<div style={styles.instructionCompactGrid}>

						<div style={styles.instructionMini}>

							<div style={styles.instructionChinese}>
								1. 心念祥和
							</div>

							<div style={styles.instructionPinyin}>
								Xīn Niàn Xiáng Hé
							</div>

							<div style={styles.instructionText}>
								Mempunyai pikiran damai
							</div>

						</div>

						<div style={styles.instructionMini}>

							<div style={styles.instructionChinese}>
								2. 端身正坐
							</div>

							<div style={styles.instructionPinyin}>
								Duān Shēn Zhèng Zuò
							</div>

							<div style={styles.instructionText}>
								Duduk tegak lurus
							</div>

						</div>

						<div style={styles.instructionMini}>

							<div style={styles.instructionChinese}>
								3. 收攝身口意
							</div>

							<div style={styles.instructionPinyin}>
								Shōu Shè Shēn Kǒu Yì
							</div>

							<div style={styles.instructionText}>
								Menjaga perilaku, ucapan,
								dan pikiran
							</div>

						</div>

						<div style={styles.instructionMini}>

							<div style={styles.instructionChinese}>
								4. 恭敬默念
							</div>

							<div style={styles.instructionPinyin}>
								Gōng Jìng Mò Niàn
							</div>

							<div style={styles.instructionText}>
								Membaca dengan hormat
								dalam hati
							</div>

						</div>

					</div>

					<div style={styles.tabletNote}>

						建議使用平板書寫以獲得更佳體驗
						<br />

						Disarankan menggunakan
						tablet agar pengalaman
						menulis lebih nyaman.

					</div>
				</div>

			)}
      {/* ================================= */}
      {/* SETTINGS MODAL */}
      {/* ================================= */}

      {showSettingsModal && (

        <div style={styles.modalOverlay}>

          <div style={styles.modalCard}>

            {/* HEADER */}

            <div style={styles.modalHeader}>

              <div style={styles.modalTitle}>
                ⚙️ 設定 Settings
              </div>

              <button
                style={styles.modalClose}
                onClick={() =>
                  setShowSettingsModal(
                    false
                  )
                }
              >
                ✕
              </button>

            </div>

            {/* PARITTA */}

            <div style={styles.modalSection}>

              <div style={styles.modalLabel}>
                經文 Paritta
              </div>

              <div style={styles.selectorWrap}>

                {data.map((g, i) => (

                  <button
                    key={i}
                    onClick={() => {

                      setParittaIndex(i);

                      setPageIndex(0);
                    }}
                    style={
                      i === parittaIndex
                        ? styles.selectorActive
                        : styles.selectorBtn
                    }
                  >
                    {g.title}
                  </button>

                ))}

              </div>

            </div>

            {/* GRID */}

            <div style={styles.modalSection}>

              <div style={styles.modalLabel}>
                格子 Grid
              </div>

              <div style={styles.gridSelector}>

                {Object.entries(
                  GRID_TYPES
                ).map(
                  ([key, label]) => (

                    <button
                      key={key}
                      onClick={() =>
                        setGridType(key)
                      }
                      style={
                        gridType === key
                          ? styles.gridActive
                          : styles.gridBtn
                      }
                    >
                      {label}
                    </button>

                  )
                )}

              </div>

            </div>

            {/* FONT */}

            <div style={styles.modalSection}>

              <div style={styles.modalLabel}>
                字體 Font Style
              </div>

              <div style={styles.gridSelector}>

                {[
                  "Cursive",
                  "KaiTi",
                  "Serif",
                ].map((f) => (

                  <button
                    key={f}
                    onClick={() =>
                      setGhostFont(f)
                    }
                    style={
                      ghostFont === f
                        ? styles.gridActive
                        : styles.gridBtn
                    }
                  >
                    {f}
                  </button>

                ))}

              </div>

            </div>

            {/* GHOST SIZE */}

            <div style={styles.modalSection}>

              <div style={styles.modalLabel}>
                字體大小 Ghost Font Size
              </div>

              <input
                type="range"
                min="2"
                max="5"
                step="0.2"
                value={ghostSize}
                onChange={(e) =>
                  setGhostSize(
                    Number(
                      e.target.value
                    )
                  )
                }
                style={styles.slider}
              />

            </div>

            {/* GRID SIZE */}

            <div style={styles.modalSection}>

              <div style={styles.modalLabel}>
                格子大小 Grid Size
              </div>

              <input
                type="range"
                min="4"
                max="8"
                step="0.2"
                value={gridSize}
                onChange={(e) =>
                  setGridSize(
                    Number(
                      e.target.value
                    )
                  )
                }
                style={styles.slider}
              />

            </div>

            {/* COLOR */}

            <div style={styles.modalSection}>

              <div style={styles.modalLabel}>
                毛筆顏色 Warna Pensil
              </div>

              <div style={styles.colorRow}>

                {[
                  "#222222",
                  "#8b0000",
                  "#1d4ed8",
                  "#0f766e",
                  "#7c3aed",
                ].map((c) => (

                  <button
                    key={c}
                    onClick={() =>
                      setPenColor(c)
                    }
                    style={{
                      ...styles.colorBtn,
                      background: c,
                      border:
                        penColor === c
                          ? "0.2rem solid #000"
                          : "0.1rem solid #ddd",
                    }}
                  />

                ))}

              </div>

            </div>

            <div style={styles.modalSection}>

              <div style={styles.modalLabel}>
                筆畫粗細 Ketebalan
              </div>

              <div style={styles.sizeRow}>

                {[4, 8, 12, 18].map(
                  (s) => (

                    <button
                      key={s}
                      onClick={() =>
                        setPenSize(s)
                      }
                      style={
                        penSize === s
                          ? styles.sizeActive
                          : styles.sizeBtn
                      }
                    >
                      {s}
                    </button>

                  )
                )}

              </div>

            </div>

            {/* CLEAR */}

            <div style={styles.modalSection}>

              {!confirmClear ? (

                <button
                  style={styles.clearBtn}
                  onClick={() =>
                    setConfirmClear(
                      true
                    )
                  }
                >
                  清除筆跡 Clear
                </button>

              ) : (

                <div style={styles.confirmBox}>

                  <div
                    style={
                      styles.confirmText
                    }
                  >
                    清除目前經文所有筆跡？
                    <br />
                    Hapus semua tulisan
                    pada paritta ini?
                  </div>

                  <div
                    style={
                      styles.confirmActions
                    }
                  >

                    <button
                      style={
                        styles.confirmYes
                      }
                      onClick={
                        clearCurrentParitta
                      }
                    >
                      是 Ya
                    </button>

                    <button
                      style={
                        styles.confirmNo
                      }
                      onClick={() =>
                        setConfirmClear(
                          false
                        )
                      }
                    >
                      否 Tidak
                    </button>

                  </div>

                </div>

              )}

            </div>

            {/* INFO */}

            <div style={styles.storageInfo}>

              <div>
                筆跡會自動保存在裝置本地瀏覽器中
                <br />
                可使用「Clear」清除目前經文筆跡
              </div>

              <div
                style={{
                  marginTop: "0.7rem",
                }}
              >
                Semua tulisan tersimpan
                di perangkat lokal dan
                tidak hilang saat
                berpindah halaman.
              </div>

            </div>

          </div>

        </div>

      )}

      {/* ================================= */}
      {/* PREVIEW */}
      {/* ================================= */}

      {!minimalMode && (

        <div style={styles.previewCard}>

          <div style={styles.previewText}>

            {currentPageChars.map(
              (char, i) => (

                <span
                  key={`${char}-${i}`}
                  style={
                    isSpaceChar(char)
                      ? styles.previewSpace
                      : undefined
                  }
                >
                  {isSpaceChar(char)
                    ? "\u3000"
                    : char}
                </span>

              )
            )}

          </div>

          <div style={styles.previewPinyin}>

            {buildPinyin(
              currentPageChars.join(
                ""
              )
            )}

          </div>

        </div>

      )}

      {/* ================================= */}
      {/* WRITING GRID */}
      {/* ================================= */}

      <div style={styles.gridContainer}>

        {currentPageChars.map(
          (char, i) => {

            if (
              isSpaceChar(char)
            ) {

              return (
                <div
                  key={`space-${i}`}
                  style={{
                    ...styles.gridSpace,
                    width: `${gridSize / 2}rem`,
                    height: `${gridSize}rem`,
                  }}
                />
              );
            }

            return (

              <WritingCell
                key={`${char}-${i}`}
                storageId={`paritta_${parittaIndex}_${pageIndex}_${i}`}
                char={char}
                gridType={gridType}
                penColor={penColor}
                penSize={penSize}
                isEraser={isEraser}
                ghostSize={ghostSize}
                gridSize={gridSize}
                ghostFont={ghostFont}
              />

            );
          }
        )}

      </div>

      {/* ================================= */}
      {/* NAVIGATION */}
      {/* ================================= */}

      <div style={styles.navRow}>

        <button
          onClick={prevPage}
          disabled={pageIndex === 0}
          style={{
            ...styles.navBtn,

            opacity:
              pageIndex === 0
                ? 0.4
                : 1,
          }}
        >
          ← 上一頁
          <br />
          Prev
        </button>

        <button
          onClick={nextPage}
          disabled={
            pageIndex >=
            currentGroup.items.length - 1
          }
          style={{
            ...styles.navBtn,

            opacity:
              pageIndex >=
              currentGroup.items.length - 1
                ? 0.4
                : 1,
          }}
        >
          下一頁 →
          <br />
          Next
        </button>

      </div>

      {/* ================================= */}
      {/* TRANSLATION */}
      {/* ================================= */}

			<div style={styles.translationCard}>

				<div style={styles.translationTitle}>
					經文翻譯 Terjemahan
				</div>

				<div style={styles.translationText}>
					{currentItem?.translation}
				</div>

			</div>

    </MasterLayoutGames>
  );
}

// ======================================================
// WRITING CELL
// ======================================================

function WritingCell({
  char,
  gridType,
  penColor,
  penSize,
  isEraser,
  storageId,
  ghostSize,
  gridSize,
  ghostFont,
}) {

  const canvasRef = useRef(null);

  useEffect(() => {

    const canvas =
      canvasRef.current;

    if (!canvas) return;

    const ctx =
      canvas.getContext("2d");

    // LOAD

    try {

      const raw =
        localStorage.getItem(
          STORAGE_KEY
        );

      let parsed = {};

      try {
        parsed = raw
          ? JSON.parse(raw)
          : {};
      } catch (e) {}

      const existing =
        parsed[storageId];

      if (existing) {

        const img = new Image();

        img.onload = () => {

          ctx.clearRect(
            0,
            0,
            canvas.width,
            canvas.height
          );

          ctx.drawImage(
            img,
            0,
            0,
            canvas.width,
            canvas.height
          );
        };

        img.src = existing;
      }

    } catch (e) {
      console.log(e);
    }

    let drawing = false;

    const getPos = (e) => {

      const rect =
        canvas.getBoundingClientRect();

      const touch =
        e.touches?.[0];

      const clientX =
        touch
          ? touch.clientX
          : e.clientX;

      const clientY =
        touch
          ? touch.clientY
          : e.clientY;

      return {

        x:
          (clientX - rect.left) *
          (canvas.width /
            rect.width),

        y:
          (clientY - rect.top) *
          (canvas.height /
            rect.height),
      };
    };

    const start = (e) => {

      drawing = true;

      const pos = getPos(e);

      ctx.beginPath();

      ctx.moveTo(
        pos.x,
        pos.y
      );
    };

    const move = (e) => {

      if (!drawing) return;

      e.preventDefault();

      const pos = getPos(e);

      ctx.lineTo(
        pos.x,
        pos.y
      );

      ctx.globalCompositeOperation =
        isEraser
          ? "destination-out"
          : "source-over";

      ctx.strokeStyle =
        isEraser
          ? "rgba(0,0,0,1)"
          : penColor;

      ctx.lineWidth =
        isEraser
          ? penSize * 2
          : penSize;

      ctx.lineCap = "round";

      ctx.lineJoin = "round";

      ctx.stroke();
    };

    const saveCanvas = () => {

      try {

        const raw =
          localStorage.getItem(
            STORAGE_KEY
          );

        let parsed = {};

        try {
          parsed = raw
            ? JSON.parse(raw)
            : {};
        } catch (e) {}

        parsed[storageId] =
          canvas.toDataURL();

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(parsed)
        );

      } catch (e) {
        console.log(e);
      }
    };

    const end = () => {

      drawing = false;

      saveCanvas();
    };

    canvas.addEventListener(
      "mousedown",
      start
    );

    canvas.addEventListener(
      "mousemove",
      move
    );

    window.addEventListener(
      "mouseup",
      end
    );

    canvas.addEventListener(
      "touchstart",
      start
    );

    canvas.addEventListener(
      "touchmove",
      move,
      { passive: false }
    );

    window.addEventListener(
      "touchend",
      end
    );

    return () => {

      canvas.removeEventListener(
        "mousedown",
        start
      );

      canvas.removeEventListener(
        "mousemove",
        move
      );

      window.removeEventListener(
        "mouseup",
        end
      );

      canvas.removeEventListener(
        "touchstart",
        start
      );

      canvas.removeEventListener(
        "touchmove",
        move
      );

      window.removeEventListener(
        "touchend",
        end
      );
    };

  }, [
    penColor,
    penSize,
    isEraser,
    storageId,
  ]);

  return (

    <div style={styles.cellWrap}>

      <div
        style={styles.cell(
          gridSize
        )}
      >

        {/* GRID */}

        {gridType === "tian" && (
          <>
            <div
              style={
                styles.crossVertical
              }
            />

            <div
              style={
                styles.crossHorizontal
              }
            />
          </>
        )}

        {gridType === "mi" && (
          <>
            <div
              style={
                styles.crossVertical
              }
            />

            <div
              style={
                styles.crossHorizontal
              }
            />

            <div
              style={
                styles.diagonal1
              }
            />

            <div
              style={
                styles.diagonal2
              }
            />
          </>
        )}

        {/* GHOST */}

        <div
          style={styles.ghostChar(
            ghostSize,
            ghostFont
          )}
        >
          {char}
        </div>

        {/* CANVAS */}

        <canvas
          ref={canvasRef}
          width={300}
          height={300}
          style={styles.writeCanvas}
        />

      </div>

      <div style={styles.cellPinyin}>
        {capitalizePinyin(
          toPinyinSync(char)
        )}
      </div>

    </div>

  );
}

// ======================================================
// STYLES
// ======================================================

const styles = {

  header: {
    marginBottom: "1.2rem",
  },

  titleChinese: {
    fontSize: "1.8rem",
    fontWeight: "700",
  },

  topActionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent:
      "space-between",
    marginBottom: "1rem",
    gap: "1rem",
  },

  secondaryTopBtn: {
    border: "none",
    borderRadius: "1rem",
    padding: "0.7rem 1rem",
    background: "#ececec",
    cursor: "pointer",
    fontSize: "0.9rem",
  },

  pageInfo: {
    fontSize: "0.95rem",
    color: "#666",
  },

  previewCard: {
    background: "#faf7f1",
    borderRadius: "1.2rem",
    padding: "1rem",
    marginBottom: "1.4rem",
  },

  previewText: {
    fontSize: "1.5rem",
    marginBottom: "0.6rem",
    lineHeight: "2.2rem",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.15rem",
  },

  previewSpace: {
    display: "inline-block",
    width: "1.6rem",
  },

  previewPinyin: {
    color: "#777",
    fontSize: "0.95rem",
    lineHeight: "1.7",
  },

  gridContainer: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fill, minmax(5.5rem, 1fr))",
    gap: "1rem",
    marginBottom: "2rem",
  },

  gridSpace: {
    width: "2rem",
    height: "5.5rem",
  },

  cellWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  cell: (gridSize) => ({
    position: "relative",
    width: `${gridSize}rem`,
    height: `${gridSize}rem`,
    border: "1px solid #999",
    background: "#fff",
    overflow: "hidden",
  }),

  writeCanvas: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    zIndex: 5,
    touchAction: "none",
  },

  ghostChar: (
    ghostSize,
    ghostFont
  ) => ({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: `${ghostSize}rem`,
    fontFamily: ghostFont,
    color:
      "rgba(0,0,0,0.12)",
    fontWeight: "500",
    userSelect: "none",
    pointerEvents: "none",
    zIndex: 1,
  }),

  crossVertical: {
    position: "absolute",
    left: "50%",
    top: 0,
    width: "1px",
    height: "100%",
    background: "#d3d3d3",
    transform:
      "translateX(-50%)",
    zIndex: 0,
  },

  crossHorizontal: {
    position: "absolute",
    top: "50%",
    left: 0,
    width: "100%",
    height: "1px",
    background: "#d3d3d3",
    transform:
      "translateY(-50%)",
    zIndex: 0,
  },

  diagonal1: {
    position: "absolute",
    left: "-10%",
    top: "50%",
    width: "120%",
    height: "1px",
    background: "#d3d3d3",
    transform: "rotate(45deg)",
    transformOrigin: "center",
    zIndex: 0,
  },

  diagonal2: {
    position: "absolute",
    left: "-10%",
    top: "50%",
    width: "120%",
    height: "1px",
    background: "#d3d3d3",
    transform:
      "rotate(-45deg)",
    transformOrigin: "center",
    zIndex: 0,
  },

  cellPinyin: {
    marginTop: "0.25rem",
    fontSize: "0.9rem",
    color: "#777",
    textAlign: "center",
  },

  navRow: {
    display: "flex",
    justifyContent:
      "space-between",
    marginBottom: "2rem",
  },

  navBtn: {
    border: "none",
    borderRadius: "1rem",
    background: "#4b5d67",
    color: "#fff",
    padding: "0.8rem 1.4rem",
    cursor: "pointer",
    fontSize: "0.95rem",
    lineHeight: "1.5",
  },

  translationCard: {
    background: "#f8f8f8",
    borderRadius: "1rem",
    padding: "1rem",
    marginBottom: "1.5rem",
  },

  translationTitle: {
    fontSize: "1rem",
    fontWeight: "600",
    marginBottom: "0.8rem",
  },

  translationText: {
    lineHeight: "1.5",
    color: "#444",
    fontSize: "1rem",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background:
      "rgba(0,0,0,0.45)",
    zIndex: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },

  modalCard: {
    width: "100%",
    maxWidth: "42rem",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "1.4rem",
    padding: "1.2rem",
  },

  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent:
      "space-between",
    marginBottom: "1rem",
  },

  modalTitle: {
    fontSize: "1.2rem",
    fontWeight: "600",
  },

  modalClose: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "1.2rem",
  },

  modalSection: {
    marginBottom: "1.5rem",
  },

  modalLabel: {
    fontSize: "0.9rem",
    marginBottom: "0.6rem",
    color: "#666",
  },

  slider: {
    width: "100%",
  },

  selectorWrap: {
    display: "flex",
    gap: "0.6rem",
    overflowX: "auto",
    paddingBottom: "0.3rem",
  },

  selectorBtn: {
    border: "none",
    borderRadius: "1rem",
    padding: "0.6rem 1rem",
    background: "#ece7dc",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: "0.95rem",
  },

  selectorActive: {
    border: "none",
    borderRadius: "1rem",
    padding: "0.6rem 1rem",
    background: "#b68b43",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: "0.95rem",
  },

  gridSelector: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },

  gridBtn: {
    border: "none",
    borderRadius: "1rem",
    padding: "0.6rem 1rem",
    background: "#ddd",
    cursor: "pointer",
    fontSize: "0.9rem",
  },

  gridActive: {
    border: "none",
    borderRadius: "1rem",
    padding: "0.6rem 1rem",
    background: "#4b5d67",
    color: "#fff",
    cursor: "pointer",
    fontSize: "0.9rem",
  },

  penGroup: {
    background: "#fafafa",
    borderRadius: "1rem",
    padding: "0.9rem",
  },

  colorRow: {
    display: "flex",
    gap: "0.5rem",
  },

  colorBtn: {
    width: "2rem",
    height: "2rem",
    borderRadius: "999rem",
    cursor: "pointer",
  },

  sizeRow: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },

  sizeBtn: {
    border: "none",
    borderRadius: "0.7rem",
    padding: "0.5rem 0.8rem",
    background: "#eee0a9",
    cursor: "pointer",
  },

  sizeActive: {
    border: "none",
    borderRadius: "0.7rem",
    padding: "0.5rem 0.8rem",
    background: "#ebb41c",
    color: "#fff",
    cursor: "pointer",
  },

  clearBtn: {
    border: "none",
    borderRadius: "1rem",
    padding: "0.8rem 1.2rem",
    background: "#a94442",
    color: "#fff",
    cursor: "pointer",
  },

  confirmBox: {
    background: "#fff5f5",
    borderRadius: "1rem",
    padding: "1rem",
  },

  confirmText: {
    marginBottom: "1rem",
    lineHeight: "1.7",
  },

  confirmActions: {
    display: "flex",
    gap: "0.8rem",
  },

  confirmYes: {
    border: "none",
    borderRadius: "0.8rem",
    padding: "0.7rem 1rem",
    background: "#c0392b",
    color: "#fff",
    cursor: "pointer",
  },

  confirmNo: {
    border: "none",
    borderRadius: "0.8rem",
    padding: "0.7rem 1rem",
    background: "#ccc",
    cursor: "pointer",
  },

  storageInfo: {
    background: "#f8f8f8",
    borderRadius: "1rem",
    padding: "1rem",
    fontSize: "0.88rem",
    color: "#666",
    lineHeight: "1.8",
  },

	topLeftActions: {
		display: "flex",
		gap: "0.7rem",
		flexWrap: "wrap",
	},

	instructionCard: {
		background: "#faf7f2",
		borderRadius: "1.2rem",
		padding: "1rem",
		marginBottom: "1rem",
		border: "1px solid #efe7da",
	},

	instructionTitle: {
		fontSize: "1rem",
		fontWeight: "600",
		marginBottom: "0.8rem",
		color: "#7a4f4f",
	},

	instructionMini: {
		paddingLeft: "0.8rem",
		paddingBottom: "0.8rem",
	},

	instructionChinese: {
		fontSize: "1.1rem",
		color: "#8b4f4f",
		marginBottom: "0.15rem",
	},

	instructionPinyin: {
		fontSize: "0.78rem",
		color: "#a17b7b",
		marginLeft: "1rem",
		marginBottom: "0.2rem",
	},

	instructionText: {
		fontSize: "0.84rem",
		color: "#555",
		lineHeight: "1.6",
		marginLeft: "1rem",
	},

	tabletNote: {
		marginTop: "1rem",
		paddingTop: "0.8rem",
		borderTop: "1px dashed #d8c8c8",
		fontSize: "0.82rem",
		color: "#777",
		lineHeight: "1.7",
		textAlign: "center",
	},
};